import { avatarColorIndex, AVATAR_BG, initialOf } from '../../lib/avatar';
import { cx } from '../../lib/cx';

export interface AvatarPerson {
  readonly id: string;
  readonly name: string;
  readonly avatarEmoji?: string | null;
  readonly avatarColor?: number | null;
  readonly isPlaceholder?: boolean;
}

export type AvatarSize = 20 | 24 | 28 | 36 | 40 | 44 | 64;

const SIZE: Record<AvatarSize, string> = {
  20: 'w-5 h-5 text-[10px]',
  24: 'w-6 h-6 text-[10px]',
  28: 'w-7 h-7 text-xs',
  36: 'w-9 h-9 text-sm',
  40: 'w-10 h-10 text-[15px]',
  44: 'w-11 h-11 text-[17px]',
  64: 'w-16 h-16 text-2xl',
};

/** DESIGN 5.5. Decorative by default: the name is always present as text nearby. */
export function Avatar({
  person,
  size = 40,
  className,
  ring,
}: {
  person: AvatarPerson;
  size?: AvatarSize;
  className?: string;
  ring?: boolean;
}) {
  const ph = person.isPlaceholder === true;
  return (
    <span
      aria-hidden
      className={cx(
        'avatar',
        SIZE[size],
        ph
          ? cx('avatar-ph', size <= 28 && '!border')
          : AVATAR_BG[avatarColorIndex(person.id, person.avatarColor)],
        ring && 'ring-2 ring-canvas',
        className,
      )}
    >
      {!ph && person.avatarEmoji ? (
        <span className="leading-none" style={{ fontSize: '1.15em' }}>
          {person.avatarEmoji}
        </span>
      ) : (
        initialOf(person.name)
      )}
    </span>
  );
}

/** Overlapping avatars, max 4 then "+N". */
export function AvatarStack({
  people,
  max = 4,
  size = 28,
}: {
  people: readonly AvatarPerson[];
  max?: number;
  size?: AvatarSize;
}) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <span className="flex -space-x-2" aria-hidden>
      {shown.map((p) => (
        <Avatar key={p.id} person={p} size={size} ring />
      ))}
      {extra > 0 && (
        <span className={cx('avatar bg-sunken text-ink-muted ring-2 ring-canvas', SIZE[size])}>
          +{extra}
        </span>
      )}
    </span>
  );
}
